/**
 * <frame-stack data-substrate="paper|canvas"
 *              data-frame="<handle>|none"
 *              data-frame-overlays='{"<handle>":"<overlay-url>", ...}'>
 *   <img class="frame-stack__art"     src="...">
 *   <img class="frame-stack__texture" hidden src="<canvas-texture-url>">
 *   <img class="frame-stack__overlay" hidden>
 * </frame-stack>
 *
 * Reacts to attribute changes by toggling the texture (canvas substrate) and
 * setting the overlay src (frame). Pure DOM, no fetch, no module deps.
 */
(function () {
  class FrameStack extends HTMLElement {
    static get observedAttributes() {
      return ['data-substrate', 'data-frame', 'data-orientation'];
    }

    connectedCallback() {
      this.art = this.querySelector('.frame-stack__art');
      this.texture = this.querySelector('.frame-stack__texture');
      this.overlay = this.querySelector('.frame-stack__overlay');
      this.applySubstrate();
      this.applyFrame();
    }

    disconnectedCallback() {
      if (this._overlayResizeObs) {
        this._overlayResizeObs.disconnect();
        this._overlayResizeObs = null;
      }
    }

    attributeChangedCallback(name) {
      if (!this.isConnected) return;
      if (name === 'data-substrate') this.applySubstrate();
      if (name === 'data-frame') this.applyFrame();
      if (name === 'data-orientation') this.applyFrame();
    }

    applySubstrate() {
      if (!this.texture) return;
      const isCanvas = this.getAttribute('data-substrate') === 'canvas';
      this.texture.hidden = !isCanvas;
    }

    applyFrame() {
      if (!this.overlay) return;
      const handle = this.getAttribute('data-frame');
      if (!handle || handle === 'none') {
        this.overlay.hidden = true;
        this.overlay.removeAttribute('src');
        this.applyOverlayRotation(false);
        return;
      }
      const map = this.readOverlaysMap();
      const entry = map[handle];
      if (entry == null) {
        console.warn('[frame-stack] frame handle not found in data-frame-overlays:', handle);
        this.overlay.hidden = true;
        this.applyOverlayRotation(false);
        return;
      }
      const requested = (this.getAttribute('data-orientation') || 'P').toUpperCase();
      // Support two shapes:
      //  - flat:   map[handle] = "https://…"           (assumed portrait artwork)
      //  - nested: map[handle] = { P: "https://…", L: "https://…" }
      let url;
      let used = 'P';  // orientation the chosen PNG was authored for
      if (typeof entry === 'string') {
        url = entry;
        used = 'P';
      } else {
        url = entry[requested];
        if (url) {
          used = requested;
        } else {
          // Fall back to the other orientation if the requested one is missing.
          const fallback = requested === 'L' ? entry.P : entry.L;
          if (fallback) {
            used = requested === 'L' ? 'P' : 'L';
            console.warn('[frame-stack] no overlay for orientation', requested, 'on handle', handle, '— falling back to', used, 'and rotating');
            url = fallback;
          } else {
            console.warn('[frame-stack] nested overlay map has no usable URL for handle', handle);
            this.overlay.hidden = true;
            this.applyOverlayRotation(false);
            return;
          }
        }
      }
      this.overlay.src = url;
      this.overlay.hidden = false;
      // If the PNG's orientation doesn't match the requested one, rotate it 90°
      // so a portrait frame still reads as a correct landscape frame (and vice versa).
      this.applyOverlayRotation(used !== requested);
    }

    // Rotate the overlay 90° to cover a container of the opposite orientation.
    // Pure CSS can't set a child's width to its parent's height, so JS sets the
    // pre-rotation box to the container's SWAPPED dimensions; after the 90° turn
    // it lands exactly over the container. Kept in sync via a ResizeObserver.
    applyOverlayRotation(rotate) {
      if (!this.overlay) return;
      if (rotate) {
        this.overlay.classList.add('frame-stack__overlay--rotated');
        this.syncRotatedOverlaySize();
        if (!this._overlayResizeObs && typeof ResizeObserver === 'function') {
          this._overlayResizeObs = new ResizeObserver(() => this.syncRotatedOverlaySize());
          this._overlayResizeObs.observe(this);
        }
      } else {
        this.overlay.classList.remove('frame-stack__overlay--rotated');
        this.overlay.style.width = '';
        this.overlay.style.height = '';
        if (this._overlayResizeObs) {
          this._overlayResizeObs.disconnect();
          this._overlayResizeObs = null;
        }
      }
    }

    syncRotatedOverlaySize() {
      if (!this.overlay) return;
      const w = this.clientWidth;
      const h = this.clientHeight;
      if (!w || !h) return;  // host still display:none — ResizeObserver re-fires on reveal
      this.overlay.style.width = h + 'px';
      this.overlay.style.height = w + 'px';
    }

    readOverlaysMap() {
      const raw = this.getAttribute('data-frame-overlays');
      if (!raw) return {};
      try {
        return JSON.parse(raw);
      } catch (err) {
        console.warn('[frame-stack] invalid data-frame-overlays JSON:', err);
        return {};
      }
    }
  }

  if (!customElements.get('frame-stack')) {
    customElements.define('frame-stack', FrameStack);
  }
})();
